/**
 * Tipos específicos do módulo de Tickets
 */

import type { CardDensity, TicketResponse, TicketStageResponse, TicketStatusCategory } from '@troia-v3/types';

// Re-export types from shared package
export type {
  Ticket,
  TicketResponse,
  TicketQuery,
  TicketListResponse,
  CreateTicketRequest,
  UpdateTicketRequest,
  AssignTicketRequest,
  ResolveTicketRequest,
  TicketStageResponse,
  TicketPipelineResponse,
  TicketStatusCategory,
  SLABehavior,
  TicketStatusCategoryConfig
} from '@troia-v3/types';

export { TICKET_STATUS_CATEGORIES } from '@troia-v3/types';

// Ticket Followers types
export type {
  TicketFollowerResponse,
  TicketFollowerType,
  TicketFollowerSource,
  AddTicketFollowerRequest,
  BulkAddTicketFollowersRequest,
} from '@troia-v3/types';

// Ticket Priority with labels
export const TICKET_PRIORITY = {
  low: { label: 'Baixa', color: '#10B981' },
  medium: { label: 'Média', color: '#3B82F6' },
  high: { label: 'Alta', color: '#F59E0B' },
  urgent: { label: 'Urgente', color: '#EF4444' },
} as const;

export type TicketPriority = keyof typeof TICKET_PRIORITY;

/**
 * Densidades OFERECIDAS no board de tickets — o seletor da toolbar recebe esta
 * lista em `options`.
 *
 * As três existem desde a tarefa C11: 'normal' é o mockup Helpdesk 07 "Relógio
 * de etapa", que mede ESTAGNAÇÃO (tempo desde `stageEnteredAt` contra o
 * `alertConfig` resolvido da etapa) e NÃO depende do motor de SLA. Os campos
 * `slaDueAt`/`slaBreached`/`slaBreachTime` continuam servindo o filtro
 * "SLA Violado", a coluna do CSV e o resumo do quadro — nada disso passa por aqui.
 *
 * O padrão do módulo continua 'detailed' (useTicketsPreferences), que é decisão
 * de preferência, não de disponibilidade.
 */
export const TICKET_CARD_DENSITIES: readonly CardDensity[] = ['detailed', 'normal', 'compact'];

// Filters específicos do módulo
export interface TicketsAdvancedFilters {
  stageId?: string[];
  statusCategory?: TicketStatusCategory[];
  priority?: TicketPriority[];
  teamId?: string;
  assigneeId?: string;
  category?: string;
  slaBreached?: boolean;
  dateRange?: {
    start: string;
    end: string;
  };
}

// Métricas do dashboard de tickets
export interface TicketsMetrics {
  total: number;
  byCategory: Record<string, number>;
  slaBreached: number;
  avgResponseTime: number;
  avgResolutionTime: number;
  priorityCounts: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
}

// Tipo para atividade do ticket (timeline)
export type TicketActivityType =
  | 'message'           // Mensagem do cliente ou agente
  | 'internal_note'     // Nota interna (não visível ao cliente)
  | 'status_change'     // Mudança de status/stage
  | 'priority_change'   // Mudança de prioridade
  | 'assignment'        // Atribuição a agente/equipe
  | 'sla_breach'        // Violação de SLA
  | 'attachment'        // Anexo adicionado
  | 'tag_added'         // Tag adicionada
  | 'tag_removed';      // Tag removida

// Atividade do ticket para timeline
export interface TicketActivity {
  id: string;
  ticketId: string;
  type: TicketActivityType;
  content?: string;
  metadata?: Record<string, any>;
  author?: {
    id: string;
    name: string;
    picture?: string;
    type: 'user' | 'contact' | 'system';
  };
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    size: number;
    mimeType: string;
  }>;
  createdAt: string;
}

// PopulatedTicket com dados relacionados
export interface PopulatedTicket extends TicketResponse {
  slaDueAt?: string;
  stage?: TicketStageResponse;
  contact?: {
    id: string;
    name: string;
    picture?: string;
  };
  assignee?: {
    id: string;
    name: string;
    picture?: string;
  };
  team?: {
    id: string;
    name: string;
  };
}
